import { useState, useEffect, useCallback, useRef } from 'react';
import { useWatch } from '../contexts/WatchContext';
import { useAuth } from '../contexts/AuthContext';
import deviceService from '../utils/services/DeviceService';

/**
 * React hook for device operations with watched user context
 */
export const useWatchedDeviceService = () => {
  const { getEffectiveUserId, isWatching, watchedUser } = useWatch();
  const { currentUserData } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const unsubscribeRef = useRef(null);

  // Initialize service
  useEffect(() => {
    deviceService.initialize();
    
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  /**
   * Get all devices for the effective user (watched or current)
   */
  const getUserDevices = useCallback(async (useCache = true) => {
    const userId = getEffectiveUserId();
    
    if (!userId) {
      setDevices([]);
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      const userDevices = await deviceService.getUserDevices(userId, useCache);
      setDevices(userDevices);
      return userDevices;
    } catch (err) {
      console.error('❌ Error fetching devices:', err);
      setError(err.message);
      
      // Check if it's an index error and log instructions
      if (err.message && err.message.includes('index')) {
        deviceService.logIndexInstructions();
      }
      
      return [];
    } finally {
      setLoading(false);
    }
  }, [getEffectiveUserId(), isWatching]);

  /**
   * Get device statistics
   */
  const getDeviceStats = useCallback(async (useCache = true) => {
    const userId = getEffectiveUserId();
    if (!userId) {
      setStats(null);
      return null;
    }

    try {
      const deviceStats = await deviceService.getDeviceStats(userId, useCache);
      setStats(deviceStats);
      return deviceStats;
    } catch (err) {
      setError(err.message);
      console.error('Error fetching device stats:', err);
      return null;
    }
  }, [getEffectiveUserId()]);

  /**
   * Create a custom token for device authentication
   */
  const createCustomToken = useCallback(async (devicePin) => {
    setError(null);

    try {
      const result = await deviceService.createCustomToken(devicePin);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Create a new device
   */
  const createDevice = useCallback(async (deviceData) => {
    const userId = getEffectiveUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    setError(null);

    try {
      const newDevice = await deviceService.createDevice({
        ...deviceData,
        uid: userId
      });
      
      // Refresh devices list
      await getUserDevices(false);
      
      return newDevice;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [getEffectiveUserId(), getUserDevices]);

  /**
   * Update an existing device
   */
  const updateDevice = useCallback(async (deviceId, updateData) => {
    setError(null);

    try {
      const updatedDevice = await deviceService.updateDevice(deviceId, updateData);
      
      // Update local state
      setDevices(prevDevices => 
        prevDevices.map(device => 
          device.key === deviceId ? updatedDevice : device
        )
      );
      
      return updatedDevice;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Delete a device
   */
  const deleteDevice = useCallback(async (deviceId) => {
    setError(null);

    try {
      await deviceService.deleteDevice(deviceId);
      
      // Update local state
      setDevices(prevDevices => 
        prevDevices.filter(device => device.key !== deviceId)
      );
      
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Delete a schedule
   */
  const deleteSchedule = useCallback(async (deviceId, schedule) => {
    setError(null);

    try {
      const updatedDevice = await deviceService.deleteSchedule(deviceId, schedule);
      
      // Update local state
      setDevices(prevDevices => 
        prevDevices.map(device => 
          device.key === deviceId ? updatedDevice : device
        )
      );
      
      return updatedDevice;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Ping a device (update last access)
   */
  const pingDevice = useCallback(async (deviceId, version = null) => {
    setError(null);

    try {
      const updatedDevice = await deviceService.pingDevice(deviceId, version);
      
      // Update local state
      setDevices(prevDevices => 
        prevDevices.map(device => 
          device.key === deviceId ? updatedDevice : device
        )
      );
      
      return updatedDevice;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Get online devices
   */
  const getOnlineDevices = useCallback(async (useCache = true) => {
    const userId = getEffectiveUserId();
    if (!userId) return [];

    try {
      return await deviceService.getOnlineDevices(userId, useCache);
    } catch (err) {
      setError(err.message);
      return [];
    }
  }, [getEffectiveUserId()]);

  /**
   * Get offline devices
   */
  const getOfflineDevices = useCallback(async (useCache = true) => {
    const userId = getEffectiveUserId();
    if (!userId) return [];

    try {
      return await deviceService.getOfflineDevices(userId, useCache);
    } catch (err) {
      setError(err.message);
      return [];
    }
  }, [getEffectiveUserId()]);

  /**
   * Subscribe to real-time device updates
   */
  const subscribeToUserDeviceScreenAssignments = useCallback((callback) => {
    const userId = getEffectiveUserId();
    if (!userId) return () => {};

    // Clean up previous subscription
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }

    // Create new subscription
    unsubscribeRef.current = deviceService.subscribeToUserDeviceScreenAssignments(userId, callback);

    return unsubscribeRef.current;
  }, [getEffectiveUserId()]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear all data
   */
  const clearData = useCallback(() => {
    setDevices([]);
    setStats(null);
    setError(null);
  }, []);

  // Load devices on mount and when user changes
  useEffect(() => {
    const loadData = async () => {
      const userId = getEffectiveUserId();
      if (!userId) {
        return;
      }
      await getUserDevices(true);
      await getDeviceStats(true);
    };
    loadData();
  }, [isWatching, watchedUser?.key, currentUserData?.key]);

  // Periodic refresh as fallback (every 30 seconds)
  useEffect(() => {
    const userId = getEffectiveUserId();
    if (!userId) {
      return;
    }

    const intervalId = setInterval(async () => {
      try {
        await getUserDevices(false); // Force fresh data
      } catch (error) {
        console.error('❌ Error during periodic device refresh:', error);
      }
    }, 30000); // 30 seconds

    return () => {
      clearInterval(intervalId);
    };
  }, [getEffectiveUserId(), getUserDevices]);

  // Set up real-time subscription to device updates
  useEffect(() => {
    const userId = getEffectiveUserId();
    if (!userId) {
      return;
    }
    
    let unsubscribe = null;
    let retryCount = 0;
    const maxRetries = 3;
    
    const setupSubscription = () => {
      try {
        // Subscribe to real-time device updates
        unsubscribe = deviceService.subscribeToUserDevices(userId, (updatedDevices, changes) => {
          setDevices(updatedDevices);
          
          // Handle specific change types
          changes.forEach(change => {
            if (change.type === 'added') {
              // Force a fresh data fetch to ensure we have the latest data
              getUserDevices(false);
            }
          });
        });
        
        retryCount = 0; // Reset retry count on successful subscription
      } catch (error) {
        console.error('❌ Error setting up device subscription:', error);
        retryCount++;
        
        if (retryCount < maxRetries) {
          console.log(`🔄 Retrying device subscription (${retryCount}/${maxRetries})...`);
          setTimeout(setupSubscription, 1000 * retryCount); // Exponential backoff
        } else {
          console.error('❌ Max retries reached for device subscription');
          setError('Failed to establish real-time connection. Please refresh the page.');
        }
      }
    };
    
    setupSubscription();

    return () => {
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }
    };
  }, [isWatching, watchedUser?.key, currentUserData?.key, getUserDevices]);

  return {
    devices,
    loading,
    error,
    stats,
    getUserDevices,
    getDeviceStats,
    createCustomToken,
    createDevice,
    updateDevice,
    deleteDevice,
    deleteSchedule,
    pingDevice,
    getOnlineDevices,
    getOfflineDevices,
    subscribeToUserDeviceScreenAssignments,
    clearError,
    clearData
  };
}; 